/**
 * Code-splitting helper for routes. The phone capture path (home, sites,
 * capture, profile) is bundled eagerly so it starts offline instantly;
 * desk-only areas (analyst review, findings, overdue, dashboard/recharts,
 * admin/template builder) are loaded on demand with `lazyPage`.
 */
import { lazy, Suspense, type ComponentType } from 'react';
import { Skeleton } from '../components';
import { isStaleVersionError, reloadOnceForNewVersion } from './PageErrorBoundary';

export function PageFallback() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

/**
 * `lazyPage(() => import('../features/admin/UsersPage'), 'UsersPage')`
 * — named exports only, so pages stay greppable.
 */
export function lazyPage<M extends Record<string, unknown>, K extends keyof M & string>(
  loader: () => Promise<M>,
  name: K,
  props: Record<string, unknown> = {},
) {
  const C = lazy(async () => {
    try {
      return { default: (await loader())[name] as ComponentType<Record<string, unknown>> };
    } catch (e) {
      // This tab runs the previous release and its page file is gone: reload once
      // for the new one (the page shows its skeleton meanwhile). Otherwise the
      // page's error boundary shows a message instead of a white screen.
      if (isStaleVersionError(e) && reloadOnceForNewVersion()) return new Promise<never>(() => {});
      throw e;
    }
  });
  return (
    <Suspense fallback={<PageFallback />}>
      <C {...props} />
    </Suspense>
  );
}
